import React, { useState, useEffect } from 'react';
import { Button, Space, Input, Select, Toast, Card, Pagination } from '@douyinfe/semi-ui';
import { IconSearch, IconPlus, IconDelete, IconEyeOpened, IconCopy } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';
import { copyToClipboard } from '../../utils/clipboard';
import ImageSelector from '../../components/ImageSelector';

const SpFiles = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 10, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const [searchStatus, setSearchStatus] = useState(null);
    const [searchType, setSearchType] = useState(null);
    const [selectedCategoryUids, setSelectedCategoryUids] = useState('');
    const [showImageSelector, setShowImageSelector] = useState(false);

    const typeMap = {
        1: { text: '图片', type: 'success' },
        2: { text: '视频', type: 'warning' },
        3: { text: '音频', type: 'info' },
        4: { text: '文档', type: 'secondary' },
        6: { text: '压缩包', type: 'danger' }
    };

    const fetchList = async (page = 1, limit = 10) => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/sp_file/list_sp_files/', {
                params: {
                    page: page - 1,
                    limit,
                    key: searchKey,
                    status: searchStatus === null ? -1 : searchStatus,
                    type: searchType,
                    category_uids: selectedCategoryUids
                }
            });
            if (response.errno === 0) {
                setDataSource(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取列表失败');
            }
        } catch (error) {
            console.error('获取列表失败:', error);
            Toast.error('获取列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const formatFileSize = (size) => {
        if (!size) return '-';
        const units = ['B', 'KB', 'MB', 'GB'];
        let fileSize = parseFloat(size);
        let unitIndex = 0;
        while (fileSize >= 1024 && unitIndex < units.length - 1) {
            fileSize /= 1024;
            unitIndex++;
        }
        return `${fileSize.toFixed(2)} ${units[unitIndex]}`;
    };

    return (
        <div className={styles.spFilesContainer}>
            <Card>
                <div className={styles.header}>
                    <Space>
                        <Input
                            placeholder="搜索文件名称"
                            value={searchKey}
                            onChange={setSearchKey}
                            style={{ width: 200 }}
                            showClear
                        />
                        <Select
                            placeholder="文件类型"
                            value={searchType}
                            onChange={setSearchType}
                            style={{ width: 150 }}
                            showClear
                        >
                            {Object.entries(typeMap).map(([value, { text }]) => (
                                <Select.Option key={value} value={parseInt(value)}>{text}</Select.Option>
                            ))}
                        </Select>
                        <Button
                            type='primary'
                            icon={<IconSearch />}
                            onClick={() => fetchList(1)}
                        >
                            搜索
                        </Button>
                        <Button
                            type='primary'
                            icon={<IconPlus />}
                            onClick={() => setShowImageSelector(true)}
                        >
                            上传素材
                        </Button>
                    </Space>
                </div>

                <ImageSelector
                    showPreview={false}
                    visible={showImageSelector}
                    onClose={() => setShowImageSelector(false)}
                    onChange={(url) => {
                        setShowImageSelector(false);
                        fetchList();
                    }}
                    previewSize={0}
                />

                <div className={styles.fileCardsContainer}>
                    {dataSource.map(record => (
                        <Card
                            key={record.file_id}
                            shadows="hover"
                            className={styles.fileCard}
                            bodyStyle={{ padding: '12px' }}
                        >
                            <div className={styles.filePreviewArea}>
                                {(record.type === 1 || record.type === 2) ? (
                                    record.type === 1 ? (
                                        <img src={record.file_url} alt={record.file_name} />
                                    ) : (
                                        <video preload={'none'} src={record.file_url} controls />
                                    )
                                ) : (
                                    <div className={styles.filePlaceholder}>暂无预览</div>
                                )}
                            </div>
                            <div className={styles.fileDetails}>
                                <div className={styles.fileMainInfo}>
                                    <div className={styles.fileName} title={record.file_name}>{record.file_name}</div>
                                    <Button
                                        type='secondary'
                                        icon={<IconCopy />}
                                        onClick={() => {
                                            copyToClipboard(record.file_url);
                                            Toast.success('链接已复制');
                                        }}
                                        size='small'
                                        className={styles.copyButton}
                                    >
                                        复制链接
                                    </Button>
                                </div>
                                {record.category_info?.name && (
                                    <div className={styles.fileCategory}>分类：{record.category_info.name}</div>
                                )}
                                {record.file_size && (
                                    <div className={styles.fileSize}>大小：{formatFileSize(record.file_size)}</div>
                                )}
                                <div className={styles.fileType}>
                                    <Button theme='light' type={typeMap[record.type]?.type || 'default'} size='small'>
                                        {typeMap[record.type]?.text || '未知'}
                                    </Button>
                                </div>
                                <div className={styles.fileTime}>创建时间：{record.create_time ? new Date(parseInt(record.create_time)).toLocaleString() : '-'}</div>
                                {!(record.type === 1 || record.type === 2) && (
                                    <Button
                                        type='primary'
                                        icon={<IconEyeOpened />}
                                        onClick={() => window.open(record.file_url, '_blank')}
                                        size='small'
                                        className={styles.previewButton}
                                    >
                                        预览
                                    </Button>
                                )}
                            </div>
                        </Card>
                    ))}
                </div>
                <div className={styles.paginationContainer}>
                    <Pagination
                        total={pagination.total}
                        currentPage={pagination.currentPage}
                        pageSize={pagination.pageSize}
                        onPageChange={(page) => fetchList(page, pagination.pageSize)}
                        onPageSizeChange={(pageSize) => fetchList(1, pageSize)}
                        showSizeChanger
                        showTotal
                    />
                </div>
            </Card>
        </div>
    );
};

export default SpFiles;